import Phaser from "phaser";
import { SceneName, GameEvents, DEV_MODE, FIRST_LEVEL_KEY } from "./constants";
import PouchDB from "pouchdb";

import art from "./artConfig.json";

import StartScene from "./gameScene";
import { getDevEmitter, dev_event } from "../devUI/devEvents";
import EditScene from "./editScene";
import { DatabaseHandle } from "./database/database";
import { Entities } from "./state/entityKinds";
import { getDevApplicationState } from "../devUI/devApplicationGlobal";
import { GameIntroCut } from "./art/transitionScenes/GameIntroCut";
import { GlobalEvents, EGlobalEvent } from "./plugins/GlobalGameEvents";
import { SoundPlayer } from "./sounds/SoundPlayer";

export default class BootGame extends Phaser.Scene {
  private progressBar: Phaser.GameObjects.Graphics;
  constructor() {
    super(SceneName.BootGame);
  }

  preload() {
    const progressBar = (this.progressBar = this.add.graphics());

    this.load.on("progress", function (value) {
      progressBar.clear();
      progressBar.fillStyle(0xdedede, 1);
      progressBar.fillRect(0, 32 + 32 / 2, 128 * value, 64 / 2);
    });

    this.load.on("complete", () => {
      progressBar.clear();
    });

    this.load.multiatlas("atlas", "assets/texture_atlas.json", "assets");
    this.load.image("tiles", "assets/tilemap.png");
    this.load.bitmapFont(
      "bitPixelFont",
      "assets/font/m3x6_0.png",
      "assets/font/m3x6.fnt"
    );

    this.load.json("levelsDump", "assets/levels/levelsDump.json");
    this.load.json("worldMap", "assets/worldMap.json");

    const backgroundMaps = [
      "worldmap_backer1",
      "worldmap_backer2",
      "worldmap_backer3",
      "worldmap_backer4",
      "worldmap_backer5",
      "worldmap_backer6",
      "worldmap_backertest",
    ];
    for (const background_url of backgroundMaps) {
      this.load.image(
        background_url,
        `assets/backgroundArt/${background_url}.png`
      );
    }
    this.load.glsl("background", "assets/shader/background.glsl");

    SoundPlayer.loadAudio(this);
  }

  create() {
    for (const [key, val] of Object.entries(art.animations)) {
      this.createAnimation(key, val);
    }
    this.sound.pauseOnBlur = false;
    const testText_prompt_render = this.add.text(-100, -24, `...`, {
      fontFamily: "sins",
      fontSize: "16px",
      align: "center",
    });

    this.time.delayedCall(500, () => {
      // If we aren't in dev mode, we hydrate database from
      // the json loaded from phaser.
      DatabaseHandle.db.info().then((info) => {
        if (info.doc_count === 0 || !DEV_MODE) {
          DatabaseHandle.db.destroy().then(() => {
            DatabaseHandle.db = new PouchDB("sokobang");
            const docs = this.cache.json.entries
              .get("levelsDump")
              .map((v) => v.doc);
            DatabaseHandle.db.bulkDocs(
              docs,
              { new_edits: false },
              (...args) => {
                if (DEV_MODE) {
                  this.scene.start(SceneName.GameControllerScene);
                } else {
                  this.scene.start(SceneName.TitleScene);
                }
              }
            );
          });
        } else {
          if (DEV_MODE) {
            this.scene.start(SceneName.GameControllerScene);
          } else {
            // Does this ever get called?
            throw new Error("should never be called...");
            this.scene.start(SceneName.TitleScene);
          }
        }
      });
    });
  }

  createAnimation(
    key: string,
    {
      GenerateFrameNames,
      Animation,
    }: {
      GenerateFrameNames: Phaser.Types.Animations.GenerateFrameNames;
      Animation: Phaser.Types.Animations.Animation;
    }
  ) {
    if (!art.atlasName) {
      throw new Error("Missing atlasName in artConfig");
    }
    if (!GenerateFrameNames) {
      throw new Error("Missing GenerateFrameNames");
    }
    if (!Animation) {
      throw new Error("Missing Animation");
    }

    const frames = this.anims.generateFrameNames(
      art.atlasName,
      GenerateFrameNames
    );

    this.anims.create({
      key,
      frames,
      ...Animation,
    });

    console.log("Created animation:", key);
  }
}

export class GameController extends Phaser.Scene {
  private currentLevel: string;
  private transitionScene: string | null;
  constructor() {
    super(SceneName.GameControllerScene);
    // TODO: Populate this from saved player state.
    this.currentLevel = FIRST_LEVEL_KEY;
    this.transitionScene = null;
  }

  create() {
    GlobalEvents.on(
      EGlobalEvent.StartSceneTransition,
      (e) => this.runTransitionScene(e),
      this
    );
    getDevEmitter()?.addListener(
      dev_event.SKIP_TO_LEVEL,
      (tilemapKey) => {
        this.scene.remove(SceneName.EditScene);
        this.scene.remove(SceneName.StartScene);
        const devState = getDevApplicationState();
        if (devState !== null) {
          devState.currentLevelKey = tilemapKey;
        }
        return DatabaseHandle.getLevel(tilemapKey).then((data: any) => {
          this.createScenePlay(data._id, data.entities as Entities[]);
        });
      },
      this
    );
    getDevEmitter()?.addListener(
      dev_event.EDIT_LEVEL,
      (tilemapKey) => {
        this.scene.remove(SceneName.EditScene);
        this.scene.remove(SceneName.StartScene);
        const devState = getDevApplicationState();
        console.log("edit with", tilemapKey);
        if (devState !== null) {
          devState.currentLevelKey = tilemapKey;
        }
        return DatabaseHandle.getLevel(tilemapKey).then((data: any) => {
          this.createSceneEdit(data._id, data.entities as Entities[]);
        });
      },
      this
    );
    if (DEV_MODE) {
      const savedLevel = [undefined, "undefined"].includes(
        sessionStorage.getItem("currentLevel")
      )
        ? "TestLevelName"
        : sessionStorage.getItem("currentLevel");

      if (savedLevel) {
        return DatabaseHandle.getLevel(savedLevel).then((data: any) => {
          this.createScenePlay(data._id, data.entities as Entities[]);
        });
      }
    }
  }

  createScenePlay(tilemapKey: string, entities: Entities[]) {
    const devState = getDevApplicationState();
    if (devState !== null) {
      devState.currentLevelKey = tilemapKey;
    }
    getDevEmitter()?.emit(dev_event.LEVEL_TRACKER, tilemapKey);
    if (DEV_MODE && tilemapKey) {
      sessionStorage.setItem("currentLevel", tilemapKey);
    }

    this.scene.add(SceneName.StartScene, StartScene, true, {
      tilemapKey: tilemapKey,
      entities,
    });
    if (this.transitionScene) {
      this.scene.swapPosition(SceneName.StartScene, this.transitionScene);
    }

    this.scene
      .get(SceneName.StartScene)
      .events.once(
        GameEvents.PlayerWonLevel,
        ({
          lvlKey,
          restartPressed,
        }: {
          lvlKey: string;
          restartPressed: Boolean;
        }) => {
          this.scene.remove(SceneName.StartScene);
          if (DEV_MODE) {
            require("toastr").success("DEV MODE! Next level would be", lvlKey);
            if (devState) {
              DatabaseHandle.getLevel(devState.currentLevelKey).then(
                (data: any) => {
                  this.createScenePlay(data._id, data.entities as Entities[]);
                }
              );
            }
          } else {
            // Actual game transition after a player won a level.
            // We want to check other data here like - did the player win the level,
            // or is it just a restart.
            console.log("Moving player to level:", lvlKey);
            // TODO: Try / catch that drops player back in overworlds...
            DatabaseHandle.getLevel(lvlKey || "Overworld_1").then(
              (data: any) => {
                this.createScenePlay(data._id, data.entities as Entities[]);
              }
            );
          }
        }
      );
  }

  createSceneEdit(tilemapKey: string, entities: Entities[]) {
    if (DEV_MODE && tilemapKey) {
      sessionStorage.setItem("currentLevel", tilemapKey);
    }

    this.scene.add(SceneName.EditScene, EditScene, true, {
      entities,
    });
  }

  runTransitionScene({
    scene,
    nextLevelKey,
  }: {
    scene: SceneName;
    nextLevelKey: string;
  }) {
    if (scene === SceneName.TransitionGameIntroCut) {
      this.transitionScene = SceneName.TransitionGameIntroCut;
      const transitionScene = this.scene.add(
        SceneName.TransitionGameIntroCut,
        GameIntroCut,
        true,
        {
          nextLevelKey,
        }
      );
      this.scene.bringToTop(SceneName.TransitionGameIntroCut);
    } else {
      throw new Error("unimplemented!");
    }
  }
}
